import React, { memo, useEffect, useState } from 'react'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import getRFPLanes, {
  getRFPLaneCount,
  IRFPLane,
  IRFPLaneFilters,
} from 'apps/marketplace/utils/api/rfpLanes'
import useFilters from 'apps/marketplace/utils/hooks/useFilters'
import { Card, Pagination } from 'transfix-ui'
import { RFPLaneTable, RFPLaneTableRow } from './RFPLaneTable'
import { SearchRFPFilters } from './SearchRFPFilters'

interface IRFPLaneSearchProps {
  selectedRFPLane?: IRFPLane
  setSelectedRFPLane: React.Dispatch<React.SetStateAction<IRFPLane | undefined>>
}

const PER_PAGE = 20
export const initialRFPLaneFilters: IRFPLaneFilters = {
  currentPage: 1,
  showPagination: false,
  perPage: PER_PAGE,
  pageCount: 0,
  pageTotalCount: 0,
}

export const RFPLaneSearch = memo<IRFPLaneSearchProps>(
  ({ selectedRFPLane, setSelectedRFPLane }) => {
    const [laneResults, setLaneResults] = useState<IRFPLane[]>([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const {
      clearFilters,
      currentPage = 1,
      onPageChange,
      pageTotalCount = 0,
      params,
      setFilters,
      setPageTotalCount,
      showPagination,
    } = useFilters(initialRFPLaneFilters)

    const getCounts = async () => {
      const { count } = await getRFPLaneCount(params)
      setPageTotalCount(count)
    }
    const getLanes = async () => {
      try {
        const { requestForProposalLanes } = await getRFPLanes(params)
        setLaneResults(requestForProposalLanes)
        setLoading(false)
      } catch (e) {
        setError(e)
        setLoading(false)
      }
    }

    // On filter change, make a new request for the RFP Lanes and their counts
    useEffect(() => {
      if (params.filters) {
        setLoading(true)
        getLanes()
        getCounts()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.filters, params.pageCount])

    const selectRFPLane = (result: IRFPLane) => {
      setSelectedRFPLane(result)
    }

    const clearResultsAndFilters = () => {
      setLaneResults([])
      setPageTotalCount(0)
      clearFilters()
      setSelectedRFPLane(undefined)
    }

    return (
      <div className='pa5'>
        <SearchRFPFilters
          filters={params.filters}
          setFilters={setFilters}
          clearFilters={clearResultsAndFilters}
          className='mb3'
        />
        {params.filters && (
          <Card title='RFP Lane Matches' className='w-90'>
            <div className='text-secondary mb3'>
              Select the desired lane from the results below.
            </div>
            <RFPLaneTable
              laneResults={laneResults}
              renderItem={(result: IRFPLane) => (
                <RFPLaneTableRow
                  result={result}
                  selectRFPLane={selectRFPLane}
                  key={result.id}
                  selectedRFPLane={selectedRFPLane}
                />
              )}
              loading={loading}
            />
            {error && (
              <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} className='mt2' />
            )}
            {showPagination && (
              <div className='tc mt3'>
                <Pagination
                  currentPage={currentPage}
                  numberOfPages={pageTotalCount}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </Card>
        )}
      </div>
    )
  }
)
